/**
 * Responsive shell building blocks (APP_SHELL). Router-agnostic: apps render their own links and use
 * `navItemClass` so active/focus styling is identical everywhere. Shell switch at 1024px (lg).
 */
import type { ReactNode } from 'react';
import { cx } from '../cx.ts';

export function navItemClass(active: boolean, collapsed = false): string {
  return cx(
    'group relative flex items-center gap-3 rounded-[12px] text-sm font-medium outline-hidden',
    'transition-[background-color,color,transform] duration-[var(--hx-duration-fast)] ease-standard',
    collapsed ? 'size-11 justify-center' : 'h-11 px-3',
    'focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-focus focus-visible:outline-offset-2',
    active
      ? 'bg-[linear-gradient(to_left,color-mix(in_srgb,var(--hx-accent)_14%,transparent),transparent_85%)] text-fg font-semibold'
      : 'text-fg-muted hover:bg-surface-2/70 hover:text-fg active:scale-[0.98]',
  );
}

/** Gold indicator bar (with a soft glow) on the inline-start edge of the active item. */
export function ActiveIndicator({ active }: { active: boolean }) {
  return (
    <span
      aria-hidden
      className={cx(
        'absolute inset-y-2.5 start-0 w-[3px] rounded-full bg-accent shadow-[0_0_12px_var(--hx-accent)]',
        'transition-[opacity,transform] duration-[var(--hx-duration-base)] ease-standard',
        active ? 'scale-y-100 opacity-100' : 'scale-y-0 opacity-0',
      )}
    />
  );
}

export function SidebarSectionLabel({
  children,
  collapsed,
}: {
  children: ReactNode;
  collapsed?: boolean;
}) {
  if (collapsed) return <hr className="mx-3 my-3 border-line" />;
  return (
    <div className="px-3 pb-1.5 pt-4 text-[11px] font-semibold tracking-wide text-fg-subtle">
      {children}
    </div>
  );
}

export function DesktopSidebar({
  header,
  children,
  footer,
  collapsed,
  label,
}: {
  header: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  collapsed?: boolean;
  label: string;
}) {
  return (
    <aside
      aria-label={label}
      className={cx(
        'sticky top-0 hidden h-dvh shrink-0 flex-col border-e border-line lg:flex',
        'bg-[linear-gradient(180deg,color-mix(in_srgb,var(--hx-bg-deep)_92%,transparent),var(--hx-bg-deep))] backdrop-blur-xl',
        'transition-[width] duration-[var(--hx-duration-slow)] ease-standard',
        collapsed ? 'w-[76px]' : 'w-[264px]',
      )}
    >
      {/* Gold hairline on the sidebar edge, fading top and bottom (landing ledger line). */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-y-[12%] end-0 w-px bg-[linear-gradient(to_bottom,transparent,color-mix(in_srgb,var(--hx-accent-soft)_55%,transparent),transparent)]"
      />
      <div className={cx('flex h-16 items-center', collapsed ? 'justify-center' : 'px-5')}>
        {header}
      </div>
      <nav className={cx('flex-1 overflow-y-auto', collapsed ? 'px-2' : 'px-3')}>{children}</nav>
      {footer && (
        <div className={cx('border-t border-line', collapsed ? 'p-2' : 'p-3')}>{footer}</div>
      )}
    </aside>
  );
}

export function TopBar({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <header
      className={cx(
        'sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-line/70 bg-canvas/75 px-4 backdrop-blur-xl backdrop-saturate-150 md:px-6',
        'pt-[env(safe-area-inset-top)]',
        className,
      )}
    >
      {children}
    </header>
  );
}

/** Mobile bottom navigation container (hidden ≥1024px). Apps supply the five items. */
export function MobileBottomNav({ children, label }: { children: ReactNode; label: string }) {
  return (
    <nav
      aria-label={label}
      data-bottom-nav
      className="fixed inset-x-3 bottom-[calc(env(safe-area-inset-bottom)+10px)] z-40 lg:hidden"
    >
      {/* Floating glass dock: rounded, blurred, a gold hairline on the top edge. */}
      <div className="hx-frame mx-auto grid h-[66px] max-w-[560px] grid-cols-5 items-center rounded-[24px] border border-line-strong/70 bg-surface-1/78 px-1.5 shadow-float backdrop-blur-2xl backdrop-saturate-150">
        {children}
      </div>
    </nav>
  );
}

export function bottomNavItemClass(active: boolean): string {
  return cx(
    'relative flex flex-col items-center gap-0.5 rounded-[16px] py-1.5 text-[10.5px] font-semibold outline-hidden',
    'transition-colors duration-[var(--hx-duration-fast)] active:scale-95',
    'focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-focus',
    // Active: a soft gold pill behind the icon, gold icon + label.
    'before:absolute before:top-1 before:h-8 before:w-12 before:rounded-full before:transition-[opacity,transform] before:duration-[var(--hx-duration-base)]',
    'before:bg-[color-mix(in_srgb,var(--hx-accent)_16%,transparent)] [&>svg]:relative [&>svg]:mt-1 [&>svg]:mb-0.5',
    active
      ? 'text-accent-text before:scale-100 before:opacity-100 [&>svg]:text-accent'
      : 'text-fg-muted before:scale-75 before:opacity-0',
  );
}

/** Raised central gold Convert action (prototype DNA): a glowing coin lifted out of the dock. */
export const centralActionClass = cx(
  'hx-disc-gold mx-auto -mt-7 grid size-[58px] place-items-center rounded-full text-on-accent outline-hidden',
  'ring-[5px] ring-canvas animate-[hx-ring_1.6s_ease-out_600ms_2] motion-reduce:animate-none',
  'transition-transform duration-[var(--hx-duration-fast)] active:scale-90 focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-focus focus-visible:outline-offset-4',
);
